import React, { useState } from "react";
import PropTypes from "prop-types";
import { useFormContext, Controller } from "react-hook-form";

import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { InputAdornment, IconButton } from "@mui/material";

import { getPaymentRedactionAttributes, getPaymentContainerRedactionAttributes } from "util/redactionHelper";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import "./Text.scss";

function Text({
  name,
  label,
  labelClass = "",
  helperText,
  type,
  sx,
  placeholder,
  isOptional,
  value: propValue,
  minValue,
  onChange: propOnChange,
  error: propError,
  size = "small",
  variant = "outlined",
  disabled = false,
  helperTextWithoutError = "",
  multiline = false,
  rows = 1,
  maxRows,
  minRows,
  startIcon,
  endIcon,
  hideHelperText = false,
  showPasswordEye = false,
  labelIcon,
  onlyAlphabets = false,
  isPayment = false,
  ...other
}) {
  const formContext = useFormContext();
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const handleChange = (event, field) => {
    const newEvent = { ...event };
    let newValue = event.target.value;

    if (type === "text" && onlyAlphabets) {
      newValue = newValue.replace(/[^A-Za-z\s]/g, "");
      newEvent.target.value = newValue;
    }

    if (type === "number" && minValue !== undefined) {
      newValue = newValue === "" ? "" : Math.max(Number(newValue), minValue);
      newEvent.target.value = newValue;
    }
    if (field) {
      field.onChange(newEvent, field);
    } else if (propOnChange) {
      propOnChange(newEvent, field);
    }
  };

  const renderTextField = (field = {}, fieldError) => {
    const paymentRedactionAttrs = isPayment ? getPaymentRedactionAttributes() : {};
    const containerRedactionAttrs = isPayment ? getPaymentContainerRedactionAttributes() : {};

    return (
      <FormControl
        sx={{ ...sx }}
        className="w-100p mui-theme-border-colors"
        data-testid="form-control"
        {...containerRedactionAttrs}
      >
        {label && (
          <InputLabel
            shrink
            htmlFor={name}
            className={`paragraph text-md text-left font-medium line-height-lg mb-025 pos-relative non-transform ${labelClass}`}
          >
            {label && `${label} ${isOptional ? "(optional)" : ""}`}
            {labelIcon && <span>{labelIcon}</span>}
          </InputLabel>
        )}

        <div className="ant-text-component">
          <TextField
            {...field}
            id={name}
            name={name}
            type={(() => {
              if (showPasswordEye && type === "password") {
                return showPassword ? "text" : "password";
              }
              return type;
            })()}
            placeholder={placeholder}
            error={!!fieldError}
            helperText={!hideHelperText && fieldError ? fieldError.message : helperText || helperTextWithoutError}
            variant={variant}
            disabled={disabled}
            fullWidth
            multiline={multiline}
            rows={rows}
            maxRows={maxRows}
            minRows={minRows}
            InputProps={{
              classes: {
                input: "paragraph font-regular text-lg line-height-xl flex",
                root: `custom-text-input-root text-field-padding text-field-input-${size} ${disabled ? "disabled-input" : ""} ${multiline ? "multiline-input" : ""}`,
                focused: "custom-text-input-focused",
                error: "custom-text-input-error",
              },
              "data-testid": "text-field",
              startAdornment: startIcon && <InputAdornment position="start">{startIcon}</InputAdornment>,
              endAdornment:
                showPasswordEye && type === "password" ? (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon className="eye-icon" />
                      ) : (
                        <VisibilityOutlinedIcon className="eye-icon" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ) : (
                  endIcon && <InputAdornment position="end">{endIcon}</InputAdornment>
                ),
              ...paymentRedactionAttrs,
            }}
            FormHelperTextProps={{
              classes: {
                root: `paragraph text-sm font-regular line-height-sm ${!helperTextWithoutError ? "error-text-color" : "helper-text-color"} ml-0`,
              },
            }}
            onChange={(e) => handleChange(e, field)}
            {...other}
          />
        </div>
      </FormControl>
    );
  };

  return formContext && !propValue && !propOnChange ? (
    <Controller
      name={name}
      control={formContext.control}
      render={({ field, fieldState: { error } }) => renderTextField(field, error)}
    />
  ) : (
    renderTextField({ value: propValue, onChange: propOnChange }, propError ? { message: helperText } : null)
  );
}

Text.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  helperText: PropTypes.string,
  helperTextWithoutError: PropTypes.string,
  type: PropTypes.string,
  sx: PropTypes.shape({}),
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minValue: PropTypes.number,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  disabled: PropTypes.bool,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  maxRows: PropTypes.number,
  minRows: PropTypes.number,
  variant: PropTypes.oneOf(["standard", "outlined", "filled"]),
  isOptional: PropTypes.bool,
  hideHelperText: PropTypes.bool,
  showPasswordEye: PropTypes.bool,
  isPayment: PropTypes.bool,
};

Text.defaultProps = {
  helperTextWithoutError: "",
  helperText: "",
  type: "text",
  sx: {},
  placeholder: "",
  value: undefined,
  minValue: undefined,
  onChange: undefined,
  error: false,
  size: "small",
  disabled: false,
  multiline: false,
  rows: 1,
  maxRows: undefined,
  minRows: undefined,
  variant: "outlined",
  isOptional: false,
  hideHelperText: false,
  showPasswordEye: false,
  isPayment: false,
};

export default Text;
